import React, { FC, useState } from "react";
import styles from "./Admin.module.scss";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import apiInstance from "../services/httpService";

const BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "https://coimbatorechamberchorale.com/"
).replace(/\/?$/, "/");

const CURRENT_YEAR = new Date().getFullYear();
const FIRST_YEAR   = 2026;
const YEAR_OPTIONS = Array.from(
  { length: CURRENT_YEAR - FIRST_YEAR + 1 },
  (_, i) => FIRST_YEAR + i
);

// Columns present in the JSON but hidden from the table view.
// Bill No. and Bill Date come from the server but are managed as editable
// frontend state — they are seeded on load and excluded from the raw column render.
const HIDDEN_COLUMNS = new Set([
  "Participant", "Status", "Transaction ID", "Settlement ID",
  "Bill No.", "Bill Date",
]);

type Half = 1 | 2;

interface HalfData {
  headers: string[];
  rows: any[][];
}

const Subscription: FC = () => {
  const [selectedYear, setSelectedYear] = useState<number>(CURRENT_YEAR);
  const [downloading,  setDownloading]  = useState(false);
  const [activeHalf,   setActiveHalf]   = useState<Half | null>(null);
  const [halfData,     setHalfData]     = useState<Record<number, HalfData>>({});
  const [halfLoading,  setHalfLoading]  = useState(false);
  const [halfError,    setHalfError]    = useState<string | null>(null);

  // Bill numbers: keyed by cacheKey (year*10+half) → row index → value.
  const [billNumbers,   setBillNumbers]   = useState<Record<number, Record<number, string>>>({});
  // Bill dates: same key structure; values are ISO date strings (YYYY-MM-DD).
  const [billDates,     setBillDates]     = useState<Record<number, Record<number, string>>>({});
  // One editingRow opens BOTH bill number and bill date fields together.
  const [editingRow,    setEditingRow]    = useState<number | null>(null);
  const [editDraft,     setEditDraft]     = useState<string>("");  // bill number draft
  const [editDateDraft, setEditDateDraft] = useState<string>("");  // bill date draft

  // Confirmation dialog — captures old/new values for both fields at once.
  const [confirmState, setConfirmState] = useState<{
    open: boolean; ri: number;
    oldNumber: string; newNumber: string;
    oldDate:   string; newDate:   string;
  }>({ open: false, ri: -1, oldNumber: "", newNumber: "", oldDate: "", newDate: "" });
  const [saving, setSaving] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean; message: string; severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const Alert = React.forwardRef(function Alert(props: any, ref: any) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  const toast = (message: string, severity: "success" | "error") =>
    setSnackbar({ open: true, message, severity });

  // ── Download Excel ──────────────────────────────────────────────────────────
  const handleDownload = async () => {
    const token = localStorage.getItem("token");
    setDownloading(true);
    try {
      const res = await fetch(`${BASE_URL}api/ccc/database/generate_excel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, year: selectedYear }),
      });
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const json = await res.json();
        toast(json.message || "Could not generate the report.", "error");
      } else {
        const blob = await res.blob();
        const url  = window.URL.createObjectURL(blob);
        const a    = document.createElement("a");
        a.href     = url;
        a.download = `ccc_subscription_${selectedYear}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast("Download started.", "success");
      }
    } catch {
      toast("Could not download the report. Please try again.", "error");
    }
    setDownloading(false);
  };

  // ── Load tab data ───────────────────────────────────────────────────────────
  const handleTabClick = (half: Half) => {
    setActiveHalf(half);
    setHalfError(null);
    setEditingRow(null);
    setEditDraft("");
    setEditDateDraft("");

    // Already fetched for this year+half combination — no refetch.
    const cacheKey = selectedYear * 10 + half;
    if (halfData[cacheKey]) return;

    const token = localStorage.getItem("token");
    setHalfLoading(true);
    apiInstance
      .axiosCall("api/ccc/subscription/data", { token, year: selectedYear, half }, "POST")
      .then((res: any) => {
        if (res?.data?.status === "success") {
          const data: HalfData = res.data.data;
          setHalfData((prev) => ({ ...prev, [cacheKey]: data }));

          // Pre-populate bill number and date state from the server response
          // so existing saved values appear immediately without re-entry.
          const billNoIdx   = data.headers.indexOf("Bill No.");
          const billDateIdx = data.headers.indexOf("Bill Date");
          const nums:  Record<number, string> = {};
          const dates: Record<number, string> = {};
          data.rows.forEach((row, ri) => {
            if (billNoIdx   >= 0 && row[billNoIdx])   nums[ri]  = String(row[billNoIdx]);
            if (billDateIdx >= 0 && row[billDateIdx]) dates[ri] = String(row[billDateIdx]);
          });
          setBillNumbers((prev) => ({ ...prev, [cacheKey]: nums }));
          setBillDates((prev)   => ({ ...prev, [cacheKey]: dates }));
        } else {
          setHalfError(res?.data?.message || "Could not load subscription data.");
        }
        setHalfLoading(false);
      })
      .catch(() => {
        setHalfError("Could not load subscription data. Please try again.");
        setHalfLoading(false);
      });
  };

  // When the year changes, reset tab selections so stale data isn't shown.
  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    setActiveHalf(null);
    setHalfError(null);
    setEditingRow(null);
    setEditDraft("");
    setEditDateDraft("");
  };

  const cacheKey  = activeHalf ? selectedYear * 10 + activeHalf : null;
  const tableData = cacheKey ? halfData[cacheKey] : null;

  // ── Status badge helper ─────────────────────────────────────────────────────
  const statusBadge = (val: any) => {
    const str = String(val ?? "").trim().toLowerCase();
    if (str === "subscribed") {
      return <span className={styles.Admin__attendance_sub_paid}>Subscribed</span>;
    }
    return <span className={styles.Admin__attendance_sub_na}>—</span>;
  };

  const participantBadge = (val: any) => {
    const str = String(val ?? "").trim().toLowerCase();
    return str === "yes"
      ? <span className={styles.Admin__attendance_sub_paid}>Yes</span>
      : <span className={styles.Admin__attendance_sub_na}>No</span>;
  };

  // ── Bill number / date helpers ──────────────────────────────────────────────
  const getBillNumber = (ri: number) =>
    cacheKey != null ? (billNumbers[cacheKey]?.[ri] ?? "") : "";

  const getBillDate = (ri: number) =>
    cacheKey != null ? (billDates[cacheKey]?.[ri] ?? "") : "";

  // Format an ISO date string (YYYY-MM-DD) to "15 Jul 2026" for display.
  const formatDate = (iso: string) => {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-").map(Number);
    const months = ["Jan","Feb","Mar","Apr","May","Jun",
                    "Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${d} ${months[m - 1]} ${y}`;
  };

  // Convert ISO string (YYYY-MM-DD) ↔ Date object without timezone shift.
  const isoToDate = (iso: string): Date | null => {
    if (!iso) return null;
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d);
  };
  const dateToIso = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  // One pencil opens both bill number and bill date for editing simultaneously.
  const startEdit = (ri: number) => {
    setEditingRow(ri);
    setEditDraft(getBillNumber(ri));
    setEditDateDraft(getBillDate(ri));
  };

  // One tick commits both; if neither changed, silently cancel.
  const commitEdit = (ri: number) => {
    if (cacheKey == null) return;
    const newNumber = editDraft.trim();
    const newDate   = editDateDraft.trim();
    const oldNumber = getBillNumber(ri);
    const oldDate   = getBillDate(ri);
    if (newNumber === oldNumber && newDate === oldDate) { cancelEdit(); return; }
    setConfirmState({ open: true, ri, oldNumber, newNumber, oldDate, newDate });
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setEditDraft("");
    setEditDateDraft("");
  };

  // Close dialog but keep edit mode open so the user can revise.
  const handleCancelConfirm = () =>
    setConfirmState((s) => ({ ...s, open: false }));

  // Call the API with both bill_number and bill_date, then update local state.
  const handleConfirm = async () => {
    if (cacheKey == null || !tableData) return;
    const { ri, oldNumber, newNumber, oldDate, newDate } = confirmState;
    const row = tableData.rows[ri];

    const colVal = (name: string) => {
      const i = tableData.headers.indexOf(name);
      return i >= 0 ? row[i] : null;
    };

    const token = localStorage.getItem("token");
    setSaving(true);
    try {
      const res = await apiInstance.axiosCall(
        "api/ccc/settlement/upsert",
        {
          token,
          user_id:      colVal("User ID"),
          settlement_id: colVal("Settlement ID"),
          payment_id:   colVal("Transaction ID"),
          bill_no:      newNumber,
          billing_date: newDate || null,
        },
        "POST"
      );
      if (res?.data?.status === "success") {
        if (newNumber !== oldNumber) {
          setBillNumbers((prev) => ({
            ...prev,
            [cacheKey]: { ...prev[cacheKey], [ri]: newNumber },
          }));
        }
        if (newDate !== oldDate) {
          setBillDates((prev) => ({
            ...prev,
            [cacheKey]: { ...prev[cacheKey], [ri]: newDate },
          }));
        }
        toast("Saved.", "success");
      } else {
        toast(res?.data?.message || "Failed to save.", "error");
      }
    } catch {
      toast("Could not save. Please try again.", "error");
    }
    setSaving(false);
    setConfirmState({ open: false, ri: -1, oldNumber: "", newNumber: "", oldDate: "", newDate: "" });
    cancelEdit();
  };

  // Indices of visible columns (those not in HIDDEN_COLUMNS).
  const visibleIndices = tableData
    ? tableData.headers
        .map((h, i) => ({ h, i }))
        .filter(({ h }) => !HIDDEN_COLUMNS.has(h))
        .map(({ i }) => i)
    : [];

  // Returns true only for rows where the hidden Status column is "Subscribed".
  // The edit icon is suppressed for all other rows (pending / not paid).
  const statusColIdx = tableData ? tableData.headers.indexOf("Status") : -1;
  const isSubscribed = (row: any[]) =>
    statusColIdx >= 0 &&
    String(row[statusColIdx] ?? "").trim().toLowerCase() === "subscribed";

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
    <div>
      {/* Controls — constrained width */}
      <div className={styles.Admin__form}>
        <label className={styles.Admin__form_label}>Subscription Report</label>
        <p style={{ fontFamily: "Montserrat-Regular", fontSize: "0.9rem", color: "#555", margin: 0 }}>
          Download the subscription report for the selected year, or browse each
          half below.
        </p>

        {/* Year + download row */}
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select
              label="Year"
              value={selectedYear}
              onChange={(e) => handleYearChange(Number(e.target.value))}
            >
              {YEAR_OPTIONS.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={<DownloadOutlinedIcon />}
            disabled={downloading}
            onClick={handleDownload}
            sx={{
              backgroundColor: "#3a8bac",
              "&:hover": { backgroundColor: "#195d7a" },
              textTransform: "none",
              fontFamily: "Montserrat-Regular",
            }}
          >
            {downloading ? "Generating…" : "Download Excel"}
          </Button>
        </div>

        {/* Half tabs */}
        <div className={styles.Admin__users_tabs}>
          {([1, 2] as Half[]).map((half) => (
            <button
              key={half}
              type="button"
              className={`${styles.Admin__users_tab} ${
                activeHalf === half ? styles.Admin__users_tab_active : ""
              }`}
              onClick={() => handleTabClick(half)}
            >
              {half === 1 ? "1st Half" : "2nd Half"}
            </button>
          ))}
        </div>
      </div>

      {/* Table — full viewport width, no horizontal scroll */}
      {activeHalf !== null && (
        <div style={{ padding: "0 1.5rem 2rem" }}>
          {halfLoading ? (
            <div className={styles.Admin__loading}>
              <CircularProgress />
            </div>
          ) : halfError ? (
            <div className={styles.Admin__empty}>{halfError}</div>
          ) : tableData ? (
            tableData.rows.length === 0 ? (
              <div className={styles.Admin__empty}>
                No subscription data for {selectedYear}{" "}
                {activeHalf === 1 ? "1st" : "2nd"} Half.
              </div>
            ) : (
              <table className={styles.Admin__attendance_table} style={{ width: "100%" }}>
                <thead>
                  <tr>
                    {visibleIndices.map((ci) => (
                      <th key={ci}>{tableData.headers[ci]}</th>
                    ))}
                    <th>Bill No.</th>
                    <th>Bill Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.rows.map((row, ri) => (
                    <tr key={ri}>
                      {visibleIndices.map((ci) => {
                        const header = tableData.headers[ci] || "";
                        const cell   = row[ci];
                        if (header === "Status")      return <td key={ci}>{statusBadge(cell)}</td>;
                        if (header === "Participant") return <td key={ci}>{participantBadge(cell)}</td>;
                        return (
                          <td key={ci} style={header === "Name" ? { textAlign: "left" } : {}}>
                            {cell ?? "—"}
                          </td>
                        );
                      })}

                      {/* Bill Number — input in edit mode, plain text otherwise */}
                      <td>
                        {editingRow === ri ? (
                          <input
                            autoFocus
                            value={editDraft}
                            onChange={(e) => setEditDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter")  commitEdit(ri);
                              if (e.key === "Escape") cancelEdit();
                            }}
                            style={{
                              width: "90px",
                              padding: "2px 6px",
                              border: "1px solid #3a8bac",
                              borderRadius: "4px",
                              fontFamily: "Montserrat-Regular",
                              fontSize: "0.83rem",
                              outline: "none",
                            }}
                          />
                        ) : (
                          <span style={{ fontFamily: "Montserrat-Regular", fontSize: "0.83rem",
                            color: getBillNumber(ri) ? "#1e293b" : "#aaa" }}>
                            {getBillNumber(ri) || "—"}
                          </span>
                        )}
                      </td>

                      {/* Bill Date — MUI DatePicker in edit mode, formatted text otherwise */}
                      <td>
                        {editingRow === ri ? (
                          <DatePicker
                            value={isoToDate(editDateDraft)}
                            onChange={(date) => {
                              if (date) setEditDateDraft(dateToIso(date as Date));
                            }}
                            closeOnSelect
                            slotProps={{
                              textField: {
                                size: "small",
                                onKeyDown: (e: React.KeyboardEvent) => {
                                  if (e.key === "Escape") cancelEdit();
                                },
                                sx: {
                                  width: "160px",
                                  "& .MuiInputBase-input": {
                                    fontFamily: "Montserrat-Regular",
                                    fontSize: "0.83rem",
                                    padding: "4px 6px",
                                  },
                                  "& .MuiOutlinedInput-root": {
                                    "& fieldset":              { borderColor: "#3a8bac" },
                                    "&:hover fieldset":        { borderColor: "#3a8bac" },
                                    "&.Mui-focused fieldset":  { borderColor: "#3a8bac" },
                                  },
                                },
                              },
                            }}
                          />
                        ) : (
                          <span style={{ fontFamily: "Montserrat-Regular", fontSize: "0.83rem",
                            color: getBillDate(ri) ? "#1e293b" : "#aaa" }}>
                            {formatDate(getBillDate(ri))}
                          </span>
                        )}
                      </td>

                      {/* Shared row action — only shown for subscribed (paid) rows */}
                      <td style={{ width: "36px", textAlign: "center" }}>
                        {editingRow === ri ? (
                          <IconButton
                            size="small"
                            onClick={() => commitEdit(ri)}
                            sx={{ color: "#2e7d32", padding: "2px" }}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        ) : isSubscribed(row) ? (
                          <IconButton
                            size="small"
                            onClick={() => startEdit(ri)}
                            sx={{ color: "#3a8bac", padding: "2px" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : null}
        </div>
      )}

      {/* Confirmation dialog — shows changes for both bill number and bill date */}
      <Dialog open={confirmState.open} onClose={handleCancelConfirm}>
        <DialogTitle sx={{ fontFamily: "Montserrat-Regular" }}>
          Confirm Changes
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            component="div"
            sx={{ fontFamily: "Montserrat-Regular", display: "flex", flexDirection: "column", gap: "8px" }}
          >
            {/* Bill number line — only shown when it changed */}
            {confirmState.newNumber !== confirmState.oldNumber && (
              <span>
                {confirmState.oldNumber
                  ? <>Bill number: <strong>"{confirmState.oldNumber}"</strong> → <strong>"{confirmState.newNumber}"</strong></>
                  : <>Store <strong>"{confirmState.newNumber}"</strong> as the bill number.</>}
              </span>
            )}
            {/* Bill date line — only shown when it changed */}
            {confirmState.newDate !== confirmState.oldDate && (
              <span>
                {confirmState.oldDate
                  ? <>Bill date: <strong>{formatDate(confirmState.oldDate)}</strong> → <strong>{formatDate(confirmState.newDate)}</strong></>
                  : <>Store <strong>{formatDate(confirmState.newDate)}</strong> as the bill date.</>}
              </span>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelConfirm}
            disabled={saving}
            sx={{ textTransform: "none", fontFamily: "Montserrat-Regular" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={saving}
            variant="contained"
            sx={{
              backgroundColor: "#3a8bac",
              "&:hover": { backgroundColor: "#195d7a" },
              textTransform: "none",
              fontFamily: "Montserrat-Regular",
            }}
          >
            {saving ? "Saving…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
    </LocalizationProvider>
  );
};

export default Subscription;
